/** @jsxImportSource @emotion/react */
import * as authSelectors from "../../auth/authSelectors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { DateFilter } from "../../../components/filters/DateFilter";
import { saveAs } from "file-saver";
import { SelectField } from "../../../components/forms/fields/SelectField";
import { DropdownButton } from "../../../components/NewButtons";
import { CardContainer } from "../../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../../components/page-skeleton/PageHeader";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { SpinnerBlock } from "../../common/Spinner";
import { Divider } from "../../../components/Divider";
import { EmptyState } from "../../../components/EmptyState";
import { CardBody } from "../../../components/page-skeleton/CardBody";
import { VerticalDivider } from "../../../components/VerticalDivider";
import { colors } from "../../../components/colors";
import { StyledText } from "../../../assets/typography/typography";
import {
  europeanCountryFlags,
  getFormattedMoneyAmountNumber,
} from "../../../util";
import {
  DataType,
  ResponsiveLineChart,
} from "../../../components/ResponsiveLineChart";
import { breakpoints } from "../../../components/breakpoints";
import moment, { Moment } from "moment";
import { ReportingPeriod } from "../models/reporting-period";
import { Country } from "../../../model/Country";
import { TotalsSuccessResponse } from "../models/reporting-success-response";
import { css } from "@emotion/react";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { MenuProps } from "antd";
import { toastr } from "react-redux-toastr";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";
import { reportingHttp, XLSXMimeType } from "../service/reports-http";
import { useEffect, useState } from "react";
import { useGetCountries } from "../../../service/api";
import { useGetShops } from "../../shop/service/shop-api";
import { useSelector } from "react-redux";
import { previewReports } from "../Reports";
import { useGetReportingData } from "../service/reports-api";
import { DotDivider } from "../../../components/DotDivider";
import { capitalize } from "lodash";

const formatDate = (date: string, period: ReportingPeriod) => {
  switch (period) {
    case "day":
      return moment(date).format("DD MMM");
    case "week":
      return moment(date).format("[Week] W");
    case "month":
      return moment(date).format("MMMM");
  }
};

const getCurrencyFlag = (currency: string, countries?: Country[]) => {
  const countriesWithCurrency = countries?.filter(
    (country) => country.currency === currency
  );
  let flag;
  if (countriesWithCurrency && countriesWithCurrency.length === 1) {
    flag = europeanCountryFlags.get(countriesWithCurrency[0].countryCode);
  }
  return flag;
};

const getFormattedData = (
  totals: TotalsSuccessResponse,
  currency: string,
  fromDate: Moment,
  toDate: Moment,
  period: ReportingPeriod
) => {
  const formattedData: DataType[] = [];

  // Generate periods
  // Using week will set first day as the sunday. IsoWeek will set it to monday.
  const firstDayInFromDatePeriod = fromDate
    .clone()
    .startOf(period === "week" ? "isoWeek" : period);
  while (firstDayInFromDatePeriod.isSameOrBefore(toDate, period)) {
    const endDate = firstDayInFromDatePeriod.clone().endOf(period);
    const amount = totals.totals.reduce((acc, next) => {
      if (
        moment(next.period).isSameOrBefore(endDate, "day") &&
        moment(next.period).isSameOrAfter(firstDayInFromDatePeriod, "day") &&
        next.realized_revenue_incl_vat.currency === currency
      ) {
        return acc + next.realized_revenue_incl_vat.amount;
      }
      return acc;
    }, 0);
    formattedData.push({
      period: formatDate(firstDayInFromDatePeriod.format(), period),
      amount: amount,
    });
    firstDayInFromDatePeriod.add(1, period);
  }
  return formattedData;
};

const cardStyleBase = css`
  display: flex;
  gap: 16px;
  padding-bottom: 24px;
  ${breakpoints.mobile} {
    flex-direction: column;
  }
  ${breakpoints.tablet} {
    flex-direction: column;
  }
`;

const bottomCardWrapper = css`
  ${cardStyleBase}
  flex-direction: column;
`;

const topCardWrapper = css`
  ${cardStyleBase}
  flex-direction: row;
`;

const additionalDotDividerStyle = css`
  ${breakpoints.mobile} {
    display: none;
  }
  ${breakpoints.tablet} {
    display: none;
  }
`;

const totalsContainerStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-right: 16px;
`;

const additionalTotalsStyles = css`
  border-radius: 9999px;
`;

const subtitleComponentWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  min-width: max-content;
  ${breakpoints.mobile} {
    flex-direction: column;
    align-items: start;
    gap: 8px;
  }
  ${breakpoints.tablet} {
    flex-direction: column;
    align-items: start;
    gap: 8px;
  }
`;

type Props = {
  reportApiUrlKey: string | undefined;
  shopId?: string;
};

const TotalsComponent = ({
  flag,
  total,
  currency,
}: {
  flag?: string;
  total: number;
  currency?: string;
}) => {
  return (
    <div css={totalsContainerStyle}>
      <VerticalDivider
        backgroundColor={colors.blueInteractive}
        additionalStyles={additionalTotalsStyles}
        width="4px"
        height="24px"
      />
      {flag && (
        <StyledText className="headerThree mb-0" color={colors.textPrimary}>
          {flag}
        </StyledText>
      )}
      <StyledText className="headerThree mb-0" color={colors.textPrimary}>
        {/* Only formatting number, so if no currency, we just use DKK */}
        {getFormattedMoneyAmountNumber(total, currency || "DKK")}
      </StyledText>
      {currency && (
        <StyledText
          className="bodyHeaderOneSemi mb-0"
          color={colors.textSecondary}
        >
          {currency}
        </StyledText>
      )}
    </div>
  );
};

const ChartFilterInfo = ({
  children,
  withDotDivider,
}: {
  children: React.ReactNode;
  withDotDivider: boolean;
}) => {
  return (
    <>
      {withDotDivider && (
        <DotDivider additionalStyles={additionalDotDividerStyle} />
      )}
      <StyledText className="bodyTextRegular mb-0">{children}</StyledText>
    </>
  );
};

export const PreviewReportDataCards = ({ reportApiUrlKey, shopId }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const [toDate, setToDate] = useState<Moment>(moment());
  const minDate = moment().month(7).year(2024).startOf("month");
  const [fromDate, setFromDate] = useState<Moment>(
    // 1st of August. Request from operations that only data from August and forward should be shown.
    minDate
  );
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  // Hiding for now until further notice by operations
  const [period, setPeriod] = useState<ReportingPeriod>("day");
  const [chosenCurrency, setChosenCurrency] = useState<string>("");
  const [chosenShop, setChosenShop] = useState<string>(shopId ?? "");
  const [revenueData, setRevenueData] = useState<Record<string, DataType[]>>(
    {}
  );
  const report = previewReports.find(
    (r) => r.reportApiUrlKey === reportApiUrlKey
  );

  const {
    data: shops,
    isLoading: shopsAreLoading,
    error: getShopsError,
  } = useGetShops(organizationKeyName, true);

  const {
    data: countries,
    isLoading: countriesAreLoading,
    error: getCountriesError,
  } = useGetCountries(true);

  const {
    data: totals,
    isLoading: totalsAreLoading,
    error,
  } = useGetReportingData(
    {
      organizationKeyName,
      fromDateTime: fromDate.format(),
      toDateTime: toDate.format(),
      period,
      format: "json",
      ...(chosenShop && { shopId: chosenShop }),
    },
    true,
    reportApiUrlKey
  );

  const filterWrapperStyle = css`
    display: flex;
    flex-direction: row;
    padding: 24px 24px 0px 24px;
    gap: 8px;
    width: auto;
    ${breakpoints.mobile} {
      flex-direction: column;
    }
    ${breakpoints.tablet} {
      flex-direction: column;
    }
  `;

  useEffect(() => {
    if (!totals?.totals || totalsAreLoading) {
      return;
    }

    const currencyTotals = totals.totals.reduce((acc, next) => {
      const existingValue = acc[next.realized_revenue_incl_vat.currency];
      if (existingValue) {
        return acc;
      }
      acc[next.realized_revenue_incl_vat.currency] = getFormattedData(
        totals,
        next.realized_revenue_incl_vat.currency,
        fromDate,
        toDate,
        period
      );
      return acc;
    }, {} as Record<string, DataType[]>);

    setRevenueData(currencyTotals);
    if (Object.entries(currencyTotals)?.length > 0) {
      if (!chosenCurrency) {
        setChosenCurrency(Object.keys(currencyTotals)[0]);
      } else {
        const currentChosenCurrencyExists = Object.keys(currencyTotals).find(
          (key) => key === chosenCurrency
        );
        if (!currentChosenCurrencyExists) {
          setChosenCurrency(Object.keys(currencyTotals)[0]);
        }
      }
    }
    // Below line is necessary, as the dependency array expect chosenCurrency to be included.
    // However, we do not want this effect to run on every change of currency, as all available currencies are already included in revenueData.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totals, totalsAreLoading, fromDate, toDate, period, setChosenCurrency]);

  const periodOptions: { value: ReportingPeriod; label: string }[] = [
    {
      label: "Daily",
      value: "day",
    },
    {
      label: "Weekly",
      value: "week",
    },
    {
      label: "Monthly",
      value: "month",
    },
  ];

  const onSubmit = async (
    fromDateTime: string,
    toDateTime: string,
    shopId?: string
  ) => {
    if (!reportApiUrlKey) {
      toastr.error("Error", "Missing report information");
      return;
    }
    setIsLoadingDownload(true);
    try {
      const response = await reportingHttp.getReportingBlob(
        {
          organizationKeyName,
          fromDateTime,
          toDateTime,
          ...(period ? { period } : {}),
          ...(shopId && { shopId }),
        },
        reportApiUrlKey
      );
      if (response.status === 204) {
        toastr.success(
          "Success",
          "This report is large, but we will send an email when it is done."
        );
        return;
      }
      const type = response.headers["content-type"] || XLSXMimeType;
      let fileName = "report.xlsx";
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const contentDispositionArray = contentDisposition.split("filename=");
        if (contentDispositionArray?.length > 1) {
          fileName = contentDispositionArray[1];
        }
      }
      const fileUrl = window.URL.createObjectURL(
        new Blob([response.data], {
          type,
        })
      );
      saveAs(fileUrl, fileName);
      toastr.success("Success", "Report generated and downloaded successfully");
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    } finally {
      setIsLoadingDownload(false);
    }
  };

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <DropdownItemButton
          onClick={async () => {
            await onSubmit(
              moment().startOf("month").hour(0).minute(0).second(0).format(),
              moment().endOf("day").hour(23).minute(59).second(59).format()
            );
          }}
          type="button"
          icon={<FontAwesomeIcon icon={solid("calendar-clock")} />}
        >
          Export report for this month for all restaurants
        </DropdownItemButton>
      ),
    },
    {
      key: "2",
      label: (
        <DropdownItemButton
          onClick={async () => {
            await onSubmit(
              moment()
                .subtract(1, "month")
                .startOf("month")
                .hour(0)
                .minute(0)
                .second(0)
                .format(),
              moment()
                .subtract(1, "month")
                .endOf("month")
                .hour(23)
                .minute(59)
                .second(59)
                .format()
            );
          }}
          type="button"
          icon={<FontAwesomeIcon icon={solid("calendars")} />}
        >
          Export report for last month for all restaurants
        </DropdownItemButton>
      ),
    },
    {
      key: "3",
      label: (
        <DropdownItemButton
          onClick={async () => {
            await onSubmit(fromDate.format(), toDate.format(), chosenShop);
          }}
          type="button"
          icon={<FontAwesomeIcon icon={solid("calendar-range")} />}
        >
          Export for chosen period for chosen restaurant
        </DropdownItemButton>
      ),
    },
  ];

  // Hiding for now until further notice by operations
  const currencyOptions = Object.entries(revenueData).map(([key]) => {
    const flag = getCurrencyFlag(key, countries);
    return { label: flag ? `${flag} ${key}` : key, value: key };
  });

  const shopOptions = (shops || [])
    .filter((shop) => {
      if (shopId) {
        return shop.id === shopId;
      } else {
        return true;
      }
    })
    .map((shop) => ({
      value: shop.id,
      label: shop.title,
    }));

  shopOptions.unshift({ value: "", label: "All restaurants" });

  const emptyStateTitle = "Export to view data";
  const emptyStateSubtitle =
    "This report does not support data preview yet. Export to view report.";

  useHandleFetchError(getShopsError);
  useHandleFetchError(getCountriesError);
  useHandleFetchError(error);

  return (
    <>
      <CardContainer overflowHidden={false}>
        <div>
          <div css={filterWrapperStyle}>
            <DateFilter
              fromDate={fromDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
              toDate={toDate}
              // 1st of August 2024. Request from operations that only data from August and forward should be shown.
              minDate={minDate.toDate()}
            />
            <SelectField
              value={shopOptions.find((option) => option.value === chosenShop)}
              width="250px"
              options={shopOptions}
              onChange={(value) => {
                const newValue = value as { value: string; label: string };
                setChosenShop(newValue.value);
              }}
            />
          </div>
          <PageHeader
            title={report?.title || "Report details"}
            subtitle={report?.description || undefined}
          >
            <DropdownButton
              menu={{
                items,
              }}
              buttonSize="large"
              leftIcon={
                isLoadingDownload ? (
                  <ActionSpinner />
                ) : (
                  <FontAwesomeIcon icon={solid("upload")} />
                )
              }
            >
              Export
            </DropdownButton>
          </PageHeader>
        </div>
        <>
          {totalsAreLoading || shopsAreLoading || countriesAreLoading ? (
            <SpinnerBlock />
          ) : (
            <>
              {!totals ? (
                <div>
                  <Divider />
                  <EmptyState
                    title={emptyStateTitle}
                    subtitle={emptyStateSubtitle}
                  />
                </div>
              ) : (
                <CardBody additionalStyles={topCardWrapper}>
                  <>
                    {/* If totals are present, we wait with Spinnerblock for useEffect to finish formatting */}
                    {Object.entries(revenueData).length === 0 ? (
                      <>
                        {totals.totals.length > 0 ? (
                          <SpinnerBlock />
                        ) : (
                          <TotalsComponent total={0} />
                        )}
                      </>
                    ) : (
                      <>
                        {Object.entries(revenueData).map(([key, value], i) => {
                          const total = value.reduce(
                            (acc, next) => acc + next.amount,
                            0
                          );
                          const flag = getCurrencyFlag(key, countries);
                          return (
                            <TotalsComponent
                              key={i}
                              total={total}
                              currency={key}
                              flag={flag}
                            />
                          );
                        })}
                      </>
                    )}
                  </>
                </CardBody>
              )}
            </>
          )}
        </>
      </CardContainer>
      {/* Hiding for now until further notice by operations */}
      {Object.entries(revenueData).length > 0 && (
        <CardContainer>
          <PageHeader
            title={report?.title || "Report details"}
            subtitleComponent={
              <div css={subtitleComponentWrapperStyle}>
                {fromDate && toDate && (
                  <ChartFilterInfo withDotDivider={false}>
                    {fromDate.format("DD MMM, YYYY")} -{" "}
                    {toDate.format("DD MMM, YYYY")}
                  </ChartFilterInfo>
                )}
                {period && (
                  <ChartFilterInfo withDotDivider>
                    {capitalize(
                      periodOptions.find((option) => option.value === period)
                        ?.label || ""
                    )}
                  </ChartFilterInfo>
                )}
                {chosenShop && (
                  <ChartFilterInfo withDotDivider>
                    {shops?.find((shop) => shop.id === chosenShop)?.title || ""}
                  </ChartFilterInfo>
                )}
              </div>
            }
          >
            {totals?.totals && totals.totals?.length > 0 && (
              <>
                <SelectField
                  value={periodOptions.find(
                    (option) => option.value === period
                  )}
                  width="150px"
                  options={periodOptions}
                  onChange={(value) => {
                    const newValue = value as { value: string; label: string };
                    setPeriod(newValue.value as ReportingPeriod);
                  }}
                />
                <SelectField
                  value={currencyOptions.find(
                    (option) => option.value === chosenCurrency
                  )}
                  width="150px"
                  options={currencyOptions}
                  onChange={(value) => {
                    const newValue = value as { value: string; label: string };
                    setChosenCurrency(newValue.value as ReportingPeriod);
                  }}
                />
              </>
            )}
          </PageHeader>
          <>
            {totalsAreLoading ? (
              <SpinnerBlock />
            ) : (
              <CardBody additionalStyles={bottomCardWrapper}>
                <ResponsiveLineChart
                  xAxisKey="period"
                  data={revenueData[chosenCurrency]}
                  currency={chosenCurrency}
                />
              </CardBody>
            )}
          </>
        </CardContainer>
      )}
      {report?.explainationBoxTitle && report.explainationBoxSubtitle && (
        <CardContainer>
          <PageHeader title={report.explainationBoxTitle} />
          <CardBody css={bottomCardWrapper}>
            <StyledText
              className="bodyTextRegular mb-0"
              color={colors.textPrimary}
            >
              {report.explainationBoxSubtitle}
            </StyledText>
          </CardBody>
        </CardContainer>
      )}
    </>
  );
};
